import { useAuthorizationServiceV1 } from "@rw1/api-services";
import { Authorization_V1 } from "@rw1/auto-generated-clients";
import { DefaultActions } from "@rw1/permissions";
import { useQuery } from "@tanstack/react-query";
import { useMemo } from "react";
import { QueryKeys } from "./QueryKeys";
import { STALE_TIME_AUTH_MS as AUTH_STALE_TIME_MS } from "./constants";

export interface AreaAccessMap {
    uam: boolean;
    company: boolean;
    insights: boolean;
    license: boolean;
}

interface UseCheckAreaAccessResult {
    access: AreaAccessMap;
    isLoading: boolean;
    error: Error | null;
}

interface Permission {
    resource: string;
    action: string;
}

const areas: Record<keyof AreaAccessMap, Permission> = {
    uam: {
        resource: "orgs/{organizationId}/uam/*",
        action: DefaultActions.Read,
    },
    company: {
        resource: "orgs/{organizationId}/company/{organizationId}",
        action: DefaultActions.Read,
    },
    insights: {
        resource: "orgs/{organizationId}/insights/*",
        action: DefaultActions.Read,
    },
    license: {
        resource: "orgs/{organizationId}/license/*",
        action: DefaultActions.Read,
    },
};

const AREA_KEYS = Object.keys(areas) as (keyof AreaAccessMap)[];

export const useCheckAreaAccess = (organizationId?: string): UseCheckAreaAccessResult => {
    const authorizationServiceV1 = useAuthorizationServiceV1();

    const { data, isLoading, error } = useQuery<Authorization_V1.AccessCheckResponse[]>({
        queryKey: [QueryKeys.AreaAccess, organizationId],
        queryFn: async () => {
            if (!organizationId) {
                throw new Error("Organization ID is required");
            }

            const requests = AREA_KEYS.map(
                (key) =>
                    new Authorization_V1.AccessCheckRequest({
                        action: areas[key].action,
                        resourceId: areas[key].resource.replaceAll("{organizationId}", organizationId),
                    })
            );

            return await authorizationServiceV1.checkAccessForCurrentUserBatch(requests);
        },
        enabled: Boolean(organizationId),
        staleTime: AUTH_STALE_TIME_MS,
        retry: false,
    });

    const access = useMemo(() => {
        if (!data || !organizationId) {
            return AREA_KEYS.reduce((acc, key) => {
                acc[key] = false;

                return acc;
            }, {} as AreaAccessMap);
        }

        const resultMap = new Map<string, boolean>();

        data.forEach((item: Authorization_V1.AccessCheckResponse) => {
            if (item.resourceId) {
                resultMap.set(item.resourceId, Boolean(item.hasAccess));
            }
        });

        return AREA_KEYS.reduce((acc, key) => {
            const expectedResourceId = areas[key].resource.replaceAll("{organizationId}", organizationId);
            acc[key] = resultMap.get(expectedResourceId) ?? false;

            return acc;
        }, {} as AreaAccessMap);
    }, [data, organizationId]);

    return {
        access,
        isLoading: isLoading || (Boolean(organizationId) && !data && !error),
        error: error ?? null,
    };
};